import { useEffect, useRef, useState } from 'react';

import nipplejs from 'nipplejs';

// Extends the keyboard controls with mobile-specific flags
export interface MobileControlsState {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
  run: boolean;
  rasengan: boolean;
  shuriken: boolean;

  // For joystick analog values
  joystickDistance: number;
  joystickAngle: number;

  // For detecting device type
  isMobile: boolean;
}

// Helper function to check if the device is mobile
const isMobile = (): boolean => {
  // Check for standard mobile user agents
  const isMobileDevice =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  // Specific detection for iPad with newer iOS versions (which can report as desktop Safari)
  const isIpadOS =
    navigator.platform === 'MacIntel' &&
    navigator.maxTouchPoints > 1 &&
    !navigator.userAgent.includes('Windows');

  return isMobileDevice || isIpadOS;
};

export const useMobileControls = () => {
  const [controls, setControls] = useState<MobileControlsState>({
    forward: false,
    backward: false,
    left: false,
    right: false,
    jump: false,
    run: false,
    rasengan: false,
    shuriken: false,
    joystickDistance: 0,
    joystickAngle: 0,
    isMobile: isMobile(),
  });

  const joystickManagerRef = useRef<any>(null);
  const joystickContainerRef = useRef<HTMLDivElement | null>(null);

  // Setup joystick if on mobile
  useEffect(() => {
    if (!controls.isMobile) return;

    // Create joystick container div if it doesn't exist
    if (!joystickContainerRef.current) {
      const container = document.createElement('div');
      container.id = 'joystick-container';
      container.style.position = 'absolute';
      container.style.left = '60px';
      container.style.bottom = '120px';
      container.style.width = '120px';
      container.style.height = '120px';
      container.style.zIndex = '1000';
      document.body.appendChild(container);
      joystickContainerRef.current = container;
    }

    // Initialize joystick
    joystickManagerRef.current = nipplejs.create({
      zone: joystickContainerRef.current,
      mode: 'static',
      position: { left: '50%', top: '50%' },
      color: 'rgba(255, 255, 255, 0.5)',
      size: 120,
    });

    // Handle joystick events
    joystickManagerRef.current.on('move', (_: any, data: any) => {
      // Convert angle to direction (forward, backward, left, right)
      const distance = data.distance / 60; // Normalize to 0-1 range
      const angle = data.angle.radian;

      // Log the angle for debugging (optional)
      // console.log('Joystick angle:', angle);

      // In nipplejs:
      // Angle is measured clockwise from the right (east) position
      // 0 = right, PI/2 = down, PI = left, 3PI/2 = up

      // In our game:
      // forward = -Z (moving up in the world)
      // backward = +Z (moving down in the world)
      // left = -X (moving left in the world)
      // right = +X (moving right in the world)

      // Fixed direction mapping with clear boundaries
      // Define quadrant ranges for more accurate direction detection
      // Each direction has its own non-overlapping range

      // Calculate unit vector components from angle
      const angleNormalized = (angle + 2 * Math.PI) % (2 * Math.PI); // Normalize to 0-2π range
      const upDownComponent = Math.cos(angleNormalized - Math.PI / 2); // Strongest up/down at π/2 and 3π/2
      const leftRightComponent = Math.cos(angleNormalized); // Strongest left/right at 0 and π

      // Apply threshold for cleaner direction changes
      const threshold = 0.5;

      setControls((prev) => ({
        ...prev,
        joystickDistance: distance,
        joystickAngle: angle,
        forward: upDownComponent > threshold, // Primarily down direction (toward buildings/away from camera)
        backward: upDownComponent < -threshold, // Primarily up direction (toward camera/bottom of screen)
        left: leftRightComponent < -threshold, // Primarily left direction (around π)
        right: leftRightComponent > threshold, // Primarily right direction (around 0)
        run: distance > 0.7, // Run if joystick is pushed far
      }));
    });

    joystickManagerRef.current.on('end', () => {
      // Reset directions when joystick is released
      setControls((prev) => ({
        ...prev,
        forward: false,
        backward: false,
        left: false,
        right: false,
        run: false,
        joystickDistance: 0,
        joystickAngle: 0,
      }));
    });

    return () => {
      if (joystickManagerRef.current) {
        joystickManagerRef.current.destroy();
      }
      if (joystickContainerRef.current) {
        document.body.removeChild(joystickContainerRef.current);
        joystickContainerRef.current = null;
      }
    };
  }, [controls.isMobile]);

  // Handle button inputs (jump, rasengan, shuriken)
  const handleButtonPress = (action: 'jump' | 'rasengan' | 'shuriken') => {
    setControls((prev) => ({ ...prev, [action]: true }));
  };

  const handleButtonRelease = (action: 'jump' | 'rasengan' | 'shuriken') => {
    setControls((prev) => ({ ...prev, [action]: false }));
  };

  return {
    controls,
    isMobile: controls.isMobile,
    handleButtonPress,
    handleButtonRelease,
  };
};
